import { useContext, useEffect, useState } from "react";
import {
  INVESTMENT_STATE,
  INVESTOR_TYPE,
  Investment,
} from "../../../types/investment_types";
import { AppContext } from "../direct-router";
import { Document, USER_STATE } from "../../../types/generic_types";
import API_CLIENT from "../../../utility/api-client";
import YouSignDialog from "../../../components/dialogs/yousign-dialog";
import IVDrawer from "../../../components/template-components/iv-drawer";
import React from "react";
import VideoIdent from "./videoident";
import { InvestmentStatusChipV2 } from "../../../components/data-display/investment-status-chip";
import AmountUtils from "../../../utility/amount-utils";
import { CopyTextV2 } from "../../../components/data-display/copy-text";
import IBAN from "iban";
import { PROVIDER } from "../../../types/fund_types";
import LoadingScreen from "../../../components/loading-screen";
import PdfPreviewer from "./pdf-preview-document";
import { DashboardType } from "../../../types/dashboard_types";
import FileUploadPopup from "../../../components/dialogs/file-upload-popup";
import { Text, Grid, Spacer, Button, Card, Description, Note, Divider } from "@geist-ui/core";
import { FileText, X, ArrowRight, ExternalLink } from "@geist-ui/icons";
import { PdfViewerGeistModal } from "../investment-process-v2/components/pdf-viewer-geist-modal";

interface InvestmentDetailsDrawerProps {
  setInvestmentId: (id: string | null) => void;
  investment_id: string | null;
  dashboard: DashboardType;
}


const DocumentRow = ({ name, url, onClick }: { name: string, url: string, onClick: (e: React.MouseEvent) => void }) => (
    <div 
        onClick={onClick}
        style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '12px 0', 
            borderBottom: '1px solid #eaeaea',
            cursor: 'pointer',
            transition: 'opacity 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText size={16} color="#666" />
            <Text font="14px" style={{ margin: 0, fontWeight: 500 }}>{name}</Text>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <ExternalLink size={16} color="#999" />
        </div>
    </div>
)

export default function InvestmentDetailsDrawer({
  investment_id,
  setInvestmentId,
  dashboard,
}: InvestmentDetailsDrawerProps) {
  const [signing_open, setSigningOpen] = useState(false);
  const open = investment_id != null;
  const app_context = useContext(AppContext);
  const [investment, setInvestment] = useState<Investment>();
  const [docs, setDocs] = useState<Document[]>();
  const [cl_docs, setClDocs] = useState<Document[]>();
  const [previewDoc, setPreviewDoc] = useState<{ url: string; name: string } | null>(
    null
  );
  const campaign = app_context.funds
    .map((fund) => fund.cashlinkCampaigns)
    .flat()
    .find((c) => c.cashlinkId == investment?.campaignId);
  const needs_videoident =
    app_context.userState == USER_STATE.ONBOARDED_1 &&
    (investment?.state == INVESTMENT_STATE.SIGNED ||
      investment?.state == INVESTMENT_STATE.SIGNATURE_PENDING);

  var co_investment = dashboard.coInvestments.find(
    (i) => i.id == investment_id
  );

  useEffect(() => {
    if (investment_id == null) return;
    API_CLIENT.getInvestment(
      app_context.token,
      investment_id,
      (investment) => setInvestment(investment),
      () => console.log("no investment found"),
      (error) => console.log(error)
    );
    API_CLIENT.getInvestmentDocs(app_context.token, investment_id, (docs) => {
      setDocs(docs);
    });
    API_CLIENT.getInvestmentDocsV1(app_context.token, investment_id, (docs) =>
      setClDocs(docs)
    );
  }, [investment_id]);

  useEffect(() => {
    if (!investment || !investment_id) return;
  }, [investment, investment_id]);

  if (co_investment) {
    return (
      <IVDrawer
        large={false}
        open={investment_id != null}
        close={() => setInvestmentId(null)}
      >
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
             <Text h3 style={{ margin: 0, fontSize: '24px', fontWeight: 700, letterSpacing: '-0.03em' }}>Investitionsdetails</Text>
             <Button icon={<X />} auto scale={0.6} onClick={() => setInvestmentId(null)} px={0} />
          </div>

          <Grid.Container gap={2} style={{ marginBottom: '24px' }}>
             <Grid xs={12}>
                <Description title="Betrag" content={`${co_investment.amount.toLocaleString("de-de")} €`} />
             </Grid>
          </Grid.Container>
          
          <Text h5 style={{ fontSize: '14px', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Dokumente</Text>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {co_investment.documents.map((doc, i) => (
                <DocumentRow 
                    key={i} 
                    name={doc.fileName.split(".")[0]} 
                    url={doc.url} 
                    onClick={() => setPreviewDoc({ url: doc.url, name: doc.fileName.split(".")[0] })}
                />
            ))}
          </div>
        </div>
      </IVDrawer>
    );
  }

  var zahlbertrag = investment && investment.investmentAmount;

  if (!zahlbertrag) return <div></div>;
  if (investment && investment.agio) {
    zahlbertrag = AmountUtils.add(zahlbertrag, investment.agio);
  }

  return (
    <div>
      {signing_open ? (
        <YouSignDialog
          signature_link={investment?.yousign_signature_link}
          is_open={signing_open}
          onClose={() => {
            setSigningOpen(false);
          }}
          onSuccess={() => {
            API_CLIENT.waitingForYousignInvestment(
              app_context.token,
              investment_id!,
              (investment: Investment) => {
                setInvestment(investment);
              },
              (err) => console.log(err)
            );
          }}
        />
      ) : null}
      <IVDrawer
        large
        open={investment_id != null}
        close={() => setInvestmentId(null)}
      >
        {investment ? (
          <div style={{ padding: '40px', paddingBottom: '80px', maxWidth: '800px', margin: '0 auto' }}>
             {/* Header */}
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
                <div>
                   <Text h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700, letterSpacing: '-0.03em' }}>Investitionsdetails</Text>
                   <Text type="secondary" font="13px" style={{ marginTop: '4px', color: '#888' }}>#{investment.id}</Text>
                </div>
                 <Button icon={<X />} auto scale={0.7} onClick={() => setInvestmentId(null)} style={{ borderRadius: '50%', width: '32px', height: '32px', minWidth: 'auto', padding: 0, border: '1px solid #eaeaea' }} />
             </div>

             {needs_videoident && (
                 <div style={{ marginBottom: '40px' }}>
                    <VideoIdent />
                 </div>
             )}

            {/* Key Value Grid */}
            <Grid.Container gap={3}>
                <Grid xs={12} sm={6}>
                    <div>
                        <Text small style={{ color: '#666', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Betrag</Text>
                        <Text h4 style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '20px' }}>{investment.amount.toLocaleString("de-de")} €</Text>
                    </div>
                </Grid>
                <Grid xs={12} sm={6}>
                    <div>
                        <Text small style={{ color: '#666', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Zahlungsbetrag</Text>
                        <Text h4 style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '20px' }}>{AmountUtils.toStringNoDigits(zahlbertrag)}</Text>
                    </div>
                </Grid>
                <Grid xs={12} sm={6}>
                    <div>
                         <Text small style={{ color: '#666', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</Text>
                         <div style={{ marginTop: '4px' }}>
                            <span style={{ display: 'inline-flex' }}>
                                <InvestmentStatusChipV2 state={investment.state} />
                            </span>
                         </div>
                    </div>
                </Grid>
                {investment.signedAt && (
                    <Grid xs={12} sm={6}>
                        <div>
                            <Text small style={{ color: '#666', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Datum</Text>
                             <Text h4 style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '20px' }}>{new Date(investment.signedAt).toLocaleDateString("de-de")}</Text>
                        </div>
                    </Grid>
                )}
                 <Grid xs={24}>
                     <div>
                        <Text small style={{ color: '#666', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Produkt</Text>
                        <Text h4 style={{ margin: '4px 0 0 0', fontWeight: 600, fontSize: '20px' }}>{investment.fundName}</Text>
                    </div>
                 </Grid>
            </Grid.Container>

            <Spacer h={3} />
            <Divider />
            <Spacer h={3} />
            
            {/* Actions / Status Messages - Prominent placement */}
             {investment.state == INVESTMENT_STATE.SIGNATURE_PENDING && (
                 <div style={{ marginBottom: '40px' }}>
                    <div style={{ background: '#fff', border: '1px solid #000', borderRadius: '8px', padding: '32px', textAlign: 'center' }}>
                        <Text h4 style={{ margin: '0 0 12px 0', fontWeight: 700 }}>Dokumente unterzeichnen</Text>
                        <Text p font="14px" style={{ margin: '0 0 24px 0', color: '#666', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                            Bitte signieren Sie die Beitrittserklärung via YouSign, um Ihre Investition abzuschließen.
                        </Text>
                        <Button 
                            auto
                            style={{ background: '#000', color: '#fff', fontWeight: 600, padding: '0 32px' }}
                            onClick={() => setSigningOpen(true)}
                        >
                            {investment.investor_type == INVESTOR_TYPE.PRIVATE_PERSON ? "Jetzt signieren" : "Zur Unterschrift"} <ArrowRight style={{ marginLeft: '8px' }} />
                        </Button>
                    </div>
                 </div>
             )}

             {investment.state == INVESTMENT_STATE.ACCEPTED && (
                 <div style={{ marginBottom: '40px' }}>
                    <Text h5 style={{ fontWeight: 700, marginBottom: '16px' }}>Zahlungsinformationen</Text>
                    <Note label={false} style={{ marginBottom: '24px' }}>
                        Bitte überweisen Sie den Betrag innerhalb von 14 Tagen.
                    </Note>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                       <Card width="100%">
                            <Text small style={{ color: '#666', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>IBAN</Text>
                            <div style={{ marginTop: '8px', fontWeight: 600 }}>
                                <CopyTextV2 text={campaign?.provider == PROVIDER.CASHLINK ? IBAN.printFormat(investment.paymentDetails.bankAccount.iban) : campaign?.bankAccountIban ? IBAN.printFormat(campaign?.bankAccountIban) : ""} />
                            </div>
                       </Card>
                       <Card width="100%">
                            <Text small style={{ color: '#666', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>BIC</Text>
                            <div style={{ marginTop: '8px', fontWeight: 600 }}>
                                <CopyTextV2 text={campaign?.provider == PROVIDER.CASHLINK ? investment.paymentDetails.bankAccount.bic : campaign?.bankAccountBic} />
                            </div>
                       </Card>
                       <Card width="100%">
                            <Text small style={{ color: '#666', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>Referenz</Text>
                            <div style={{ marginTop: '8px', fontWeight: 600 }}>
                                <CopyTextV2 text={campaign?.provider == PROVIDER.CASHLINK ? investment.paymentDetails?.paymentCode || "" : `${app_context.user?.natural_person.forename}, ${app_context.user?.natural_person.surname}`} />
                            </div>
                       </Card>
                    </div>
                 </div>
             )}


            {/* Documents List */}
            <div style={{ marginBottom: '24px' }}>
                <Text h5 style={{ fontSize: '14px', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Dokumente</Text>
                <div style={{ borderTop: '1px solid #eaeaea' }}>
                    {campaign?.documents.map((doc, i) => (
                        <DocumentRow 
                            key={`camp-${i}`} 
                            name={doc.name} 
                            url={doc.location} 
                            onClick={() => setPreviewDoc({ url: doc.location, name: doc.name })}
                        />
                    ))}
                    {docs?.map((doc, i) => (
                        <DocumentRow 
                            key={`doc-${i}`} 
                            name={doc.description || doc.name} 
                            url={doc.location} 
                            onClick={() => setPreviewDoc({ url: doc.location, name: doc.description || doc.name })}
                        />
                    ))}
                    {cl_docs?.map((cl_doc, i) => (
                        <DocumentRow 
                            key={`cl-${i}`} 
                            name={cl_doc.name.split(".")[0].replaceAll("_", " ")} 
                            url={cl_doc.location} 
                            onClick={() => setPreviewDoc({ url: cl_doc.location, name: cl_doc.name.split(".")[0].replaceAll("_", " ") })}
                        />
                    ))}
                </div>
            </div>

             <Spacer h={2} />
             
             <div style={{ textAlign: 'center' }}>
                <FileUploadPopup
                  investmentId={investment.id}
                  onDocumentUploaded={() => {
                    investment_id &&
                      API_CLIENT.getInvestmentDocs(
                        app_context.token,
                        investment_id,
                        (docs) => {
                          setDocs(docs);
                        }
                      );
                  }}
                  trigger={
                      <Button auto type="secondary-light" scale={0.8} style={{ color: '#666' }}>Dokument beifügen</Button>
                  }
                />
            </div>

          </div>
        ) : (
          <LoadingScreen className="h-full w-full" />
        )}
      </IVDrawer>

      <PdfViewerGeistModal 
          isOpen={previewDoc !== null}
          onClose={() => setPreviewDoc(null)}
          url={previewDoc?.url || null}
          name={previewDoc?.name || null}
      />
    </div>
  );
}
